import * as Sentry from '@sentry/react'
import add from 'date-fns/add'
import format from 'date-fns/format'
import { useEffect, useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import type { TBookingContext, TBookingGroup } from '@providers/booking/booking'
import { Types } from 'service-api'
import { useClientBookingReservation } from 'service-api/src/hooks'

const DATE_TIME_FORMAT = 'yyyy-MM-dd'

export const useBookingReservation = (locationName: string) => {
  const bookingCtx = useBookingContext()

  useEffect(() => {
    if (typeof bookingCtx.state.startDate === 'undefined') {
      return
    }
    if (Boolean(bookingCtx.state.data.serviceType?.serviceType?.name) === false) {
      Sentry.addBreadcrumb({
        category: 'booking',
        message: 'serviceTypeName is undefined',
        level: 'debug',
      })
      Sentry.setContext('serviceType', {
        st1: bookingCtx.state.data.serviceType?.name,
        st2: bookingCtx.state.data.serviceType?.serviceType?.name,
        temper: Boolean(bookingCtx.state.data.temperTest),
        groups: JSON.stringify(bookingCtx.state.groups, null, 2),
        config: {
          booking: Boolean(bookingCtx.state.config?.booking),
          breedsBySpecie: Boolean(bookingCtx.state.config?.breedsBySpecie),
          location: Boolean(bookingCtx.state.config?.location),
          serviceType: Boolean(bookingCtx.state.config?.serviceType),
          species: Boolean(bookingCtx.state.config?.species),
        },
      })
      Sentry.captureException(new Error('serviceTypeName is undefined'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.data.serviceType?.serviceType?.name])

  const bookingOrder: Types.CreateBookingOrderInput | undefined = useMemo(() => {
    if (typeof bookingCtx.state.startDate === 'undefined') {
      return
    }
    const serviceTypeName = bookingCtx.state.data.serviceType?.serviceType?.name || 'boarding'

    return {
      serviceTypeName: serviceTypeName!,
      pets: buildPets(bookingCtx.state.groups),
      reservations: [
        ...buildReservation(bookingCtx.state, serviceTypeName!),
        ...buildTemperTestReservations(bookingCtx.state),
      ],
    }
  }, [bookingCtx.state])

  return useClientBookingReservation(locationName, bookingOrder)
}

const buildPets = (groups: TBookingGroup[]) =>
  groups
    .map((group) =>
      group.pets.map((p) => ({
        petId: p.id.length < 12 ? undefined : p.id,
        displayName: p.name,
        locationSpeciesId: group.specieId,
        breedId: p.breedId,
        weight: p.weight ? parseFloat(p.weight) : undefined,
        temperTested: p.temperTested,
      }))
    )
    .flat()

const getDateRanges = (state: TBookingContext) => {
  if (!state.startDate) {
    return []
  }
  if (state.kind === 'daycare') {
    return state.cart.groups
      .map((group) => {
        return group.daycareSelected!.map(({ date }) => ({
          startDate: format(date, DATE_TIME_FORMAT),
          endDate: format(add(date, { days: 1 }), DATE_TIME_FORMAT),
        }))
      })
      .flat()
  }
  return [
    {
      startDate: format(state.startDate!, DATE_TIME_FORMAT),
      endDate: format(state.endDate!, DATE_TIME_FORMAT),
    },
  ]
}

const buildReservation = (state: TBookingContext, serviceTypeName: string) => {
  const dateRanges = getDateRanges(state)

  return dateRanges
    .map((dr) => {
      let counter = 0
      return state.cart.groups.map((group, cIdx) => {
        const groupPets = state.groups[cIdx].pets
        const petLength = groupPets.length
        const petIndexes = Array.from(new Array(petLength).fill(0), (v, i) => v + i + counter)
        counter += petLength

        return {
          serviceTypeName,
          petIndexes,
          startDate: dr.startDate,
          endDate: dr.endDate,
          primaryOffers: [
            {
              id: group.offerSelected.offer.id!,
              qty: 1,
              serviceOffers: group.roomsAddOn?.map((addOn) => ({ id: addOn.offer.id!, qty: 1 })) || [],
              petOffers: (
                group.petsAddOn?.map((addOn) =>
                  addOn.pets.map((pet) => ({
                    id: addOn.offer.id!,
                    qty: pet.qty,
                    petIndex: petIndexes?.[groupPets.findIndex((p) => p.id === pet.id)],
                  }))
                ) || []
              ).flat(),
            },
          ],
        }
      })
    })
    .flat()
}

// We can not filter group by temperTest because we need to keep the index to build
// the petIndex, we filter at the end by null
export const buildTemperTestReservations = (state: TBookingContext) => {
  let ctr = 0
  const petIndexes: number[] = state.groups.reduce((acc, group) => {
    group.pets.forEach((pet) => {
      if (!pet.temperTested) {
        acc.push(ctr)
      }
      ctr++
    })
    return acc
  }, [] as number[])

  if (petIndexes.length === 0) {
    return []
  }

  const temperTest = state.cart.temperTest!
  return [
    {
      serviceTypeName: state.data.temperTest?.serviceType?.name,
      petIndexes,
      startDate: format(temperTest.date!, DATE_TIME_FORMAT),
      endDate: format(add(temperTest.date!, { days: 1 }), DATE_TIME_FORMAT),
      startTime: temperTest.timez!,
      primaryOffers: [
        {
          id: state.data.temperTestOffer?.id || '',
          qty: 1,
          serviceOffers: [],
          petOffers: [],
        },
      ],
    },
  ]
}
